import { useState, type ReactNode } from 'react';
import { nip19 } from 'nostr-tools';
import { ContactDialog } from '@/components/ContactDialog';
import { useAuthor } from '@/hooks/useAuthor';
import { ARCHIVE_PUBKEY } from '@/hooks/useArchiveNotes';
import { DEFAULT_SUMMARY, cleanAbout, profileUrlFor } from '@/lib/archiveProfile';

interface ContactLauncherProps {
  /** Renders the trigger (a button, a link…); call `open` to show the panel. */
  children: (open: () => void) => ReactNode;
}

/**
 * Wraps any trigger with the Contact / About panel, so Contact can be offered
 * from the header bar, the footer or anywhere else without lifting state or
 * passing the archive's profile around.
 */
export function ContactLauncher({ children }: ContactLauncherProps) {
  const [open, setOpen] = useState(false);
  const author = useAuthor(ARCHIVE_PUBKEY);
  const metadata = author.data?.metadata;
  const npub = nip19.npubEncode(ARCHIVE_PUBKEY);
  const about = metadata?.about ? cleanAbout(metadata.about) : '';

  return (
    <>
      {children(() => setOpen(true))}
      <ContactDialog
        open={open}
        onOpenChange={setOpen}
        title={metadata?.display_name ?? metadata?.name ?? 'Anonymous Archive'}
        picture={metadata?.picture}
        about={about || DEFAULT_SUMMARY}
        npub={npub}
        profileUrl={profileUrlFor(npub)}
      />
    </>
  );
}
